import { useState } from 'react';

import {
  AddPreviousCampaignToDealMutation,
  AddPreviousCampaignToDealMutationVariables,
  DatasourceInstructionMode,
  GetDealItemsByDealIdDocument,
  useAddPreviousCampaignToDealMutation,
} from '@/lib/graphql/generated/graphql';
import { trackEvent } from '@/lib/mixpanel';
import { DomainError } from '@/types/data-source';
import { MutationHookOptions } from '@apollo/client';

import { useDealNotEditableHandler } from './useDealNotEditableHandler';

export const useAddPreviousCampaignToDeal = (
  options?: Omit<
    MutationHookOptions<
      AddPreviousCampaignToDealMutation,
      AddPreviousCampaignToDealMutationVariables
    >,
    'variables'
  >
) => {
  const [domainError, setDomainError] = useState<DomainError | null>(null);
  const { handleIfDealNotEditable } = useDealNotEditableHandler();
  const [addPreviousCampaignToDealMutation, { loading, error }] =
    useAddPreviousCampaignToDealMutation({
      ...(options ?? {}),
    });

  const addPreviousCampaignToDeal = async (
    dealId: string,
    saleId: string,
    instructionMode?: DatasourceInstructionMode
  ): Promise<{ __typename?: 'MutationSuccess'; id: string } | undefined> => {
    setDomainError(null);
    trackEvent('IMPORT_STARTED', { dealId, source: 'previousCampaign' });

    const result = await addPreviousCampaignToDealMutation({
      variables: { dealId, saleId, instructionMode },
      refetchQueries: [
        { query: GetDealItemsByDealIdDocument, variables: { dealId } },
      ],
    });

    const mutationResult = result.data?.addPreviousCampaignToDeal;

    if (mutationResult?.__typename === 'MutationSuccess') {
      trackEvent('IMPORT_COMPLETED', {
        dealId,
        source: 'previousCampaign',
      });
      return mutationResult;
    }
    if (mutationResult?.__typename === 'DomainException') {
      const errorCode = mutationResult.code;
      trackEvent('IMPORT_FAILED', {
        dealId,
        source: 'previousCampaign',
        errorCode,
      });
      if (handleIfDealNotEditable(errorCode)) return undefined;
      setDomainError({
        code: errorCode,
        message: `${errorCode}`,
      });
      return undefined;
    }
    trackEvent('IMPORT_FAILED', {
      dealId,
      source: 'previousCampaign',
      errorCode: 'IMPORT_FAILED',
    });
    setDomainError({
      code: 'IMPORT_FAILED',
      message: 'Unknown mutation result',
    });
    return undefined;
  };

  return {
    addPreviousCampaignToDeal,
    loading,
    error,
    domainError,
    isDomainError: Boolean(domainError),
  };
};
